import {
  put,
  all,
  takeEvery,
  fork,
  select,
  call,
  delay
} from "redux-saga/effects";
import * as R from "ramda";
import { renderDropZone } from "utils/General/sagas";
import { actions, getters } from "../model";
import { registerError } from "redux/modules/errors/actions";
import { showSnackbar } from "redux/modules/snackbar/actions";
import Helpers from "utils/Global/Helpers";
import { ACCEPT_UPLOAD_FILE } from "utils/file-types-upload";
import { eventDetails as getEventDetails } from "redux/modules/event/selectors";
import { eventDetails as getPortalEventDetails } from "redux/modules/portal/selectors";
import { getAllowedFileTypes, getCountOfMaxFilesToUpload } from "../selectors";
import { apiCall } from "App/Data/sagas";

import {
  EDIT_FILE_TYPE_FIELD_ID,
  MAX_FILE_SIZE_KB,
  MEASURE
} from "EventLight/Sessions/SessionContent/constants";

const getParams = function*() {
  const viaPortal = yield select(getters.viaPortal);
  const eventDetails = yield select(getEventDetails);
  const portalEventDetails = yield select(getPortalEventDetails);

  return {
    eventDetails: viaPortal ? portalEventDetails : eventDetails,
    viaPortal
  };
};

const processUpload = function*({ files }) {
  yield put(actions.setFileUploadUploadHasFinished(false));
  const data = yield select(getters.fileUploadData);
  const formatted = files
    .map(file => ({
      uploaded: true,
      url: `https://content-cdn.sessionboard.com/${file.key}`,
      is_external: false,
      title: file.filename,
      key: file.key,
      filename: file.filename,
      mimetype: file.mimetype,
      size: file.size,
      payload: file,
      uploadId: file.uploadId,
      type: "presentation",
      version: null
    }))
    .concat(data);

  yield all([put(actions.setFileUploadData(formatted))]);
};

const isValueInMediaTypes = value => {
  if (value === "all") {
    return Object.values(ACCEPT_UPLOAD_FILE).flat();
  }
  const mediaCategories = ["image", "video", "audio"];
  for (const category of mediaCategories) {
    if (category === value) {
      return `${category}/*`;
    }
  }
  return false;
};

const init = function*({
  payload: {
    onFileUploadProgress,
    onFileUploadCancel,
    onFileUploadFailed,
    onFileUploadFinished,
    settings = {}
  }
}) {
  const allowedFileTypes = yield select(getAllowedFileTypes);
  const acceptedMimeTypes = R.flatten(
    allowedFileTypes.map(type => {
      const isMedia = isValueInMediaTypes(type);
      return isMedia ? [isMedia] : ACCEPT_UPLOAD_FILE[type];
    })
  );

  try {
    document.getElementById(`dropzone-container-without-modal`).innerHTML = "";

    const {
      min_size: iniMinFileSize = 0,
      max_size: iniMaxFileSize = MAX_FILE_SIZE_KB,
      size_type: initFileSizeType = "kb"
    } = settings;

    const customText = `Drag and drop a file here or click to choose one from your computer. We accept files between ${iniMinFileSize} ${initFileSizeType} and ${iniMaxFileSize} ${initFileSizeType} `;

    const newMinFileSize = R.propOr(null, initFileSizeType, {
      kb: MEASURE * iniMinFileSize,
      mb: MEASURE * MEASURE * iniMinFileSize,
      gb: MEASURE * MEASURE * MEASURE * iniMinFileSize
    });

    const newMaxFileSize = R.propOr(null, initFileSizeType, {
      kb: MEASURE * iniMaxFileSize,
      mb: MEASURE * MEASURE * iniMaxFileSize,
      gb: MEASURE * MEASURE * MEASURE * iniMaxFileSize
    });

    const onFileSelected = file => {
      // file.size is in bytes
      if (file.size > newMaxFileSize) {
        throw new Error(
          `File too big, select something smaller than ${iniMaxFileSize +
            " " +
            initFileSizeType}`
        );
      }
      if (file.size < newMinFileSize) {
        throw new Error(
          `File too small, select something bigger than ${iniMinFileSize +
            " " +
            initFileSizeType}`
        );
      }
    };

    for (;;) {
      const files = yield call(renderDropZone, {
        multiple: true,
        dropPane: {
          showProgress: false
        },
        fromSources: ["local_file_system"],
        container: `dropzone-container-without-modal`,
        accept: acceptedMimeTypes,
        storeTo: {
          path: "content/"
        },
        uploadConfig: {
          progressInterval: 500
        },
        onFileUploadProgress,
        onFileUploadCancel,
        onFileUploadFailed,
        onFileUploadFinished,
        customText,
        onFileSelected
      });

      yield call(processUpload, { files });

      document.getElementById(`dropzone-container-without-modal`).innerHTML =
        "";
    }
  } catch (error) {
    yield put(
      registerError([
        {
          system: error,
          user: "An error occurred while rendering drop zone"
        }
      ])
    );
  }
};

const cancelUpload = function*({ payload: uploadId }) {
  const progress = yield select(getters.fileUploadProgress);
  const fileUploadFiles = yield select(getters.fileUploadFiles);
  const fileUploadData = yield select(getters.fileUploadData);

  const updatedProgress = R.omit([uploadId], progress);

  yield all(
    [
      put(actions.setFileUploadProgress(updatedProgress)),
      put(actions.setFileUploadFiles(R.omit([uploadId], fileUploadFiles))),
      put(
        actions.setFileUploadData(
          fileUploadData.filter(d => d.uploadId !== uploadId),
          fileUploadData
        )
      ),
      R.isEmpty(updatedProgress)
        ? put(actions.setFileUploadUploadHasFinished(false))
        : null
    ].filter(v => v)
  );
};

const validateUrl = function*({ payload }) {
  const isValidUrl = Helpers.isValidHTTPSURL(payload);

  yield all([
    yield put(actions.setFileUploadUrl(payload)),
    yield put(
      actions.setFileUploadUrlMessage(
        R.isEmpty(payload) || isValidUrl ? "" : "not valid"
      )
    ),
    yield put(actions.setFileUploadIsValidUrl(isValidUrl))
  ]);
};

const addUrl = function*() {
  const url = yield select(getters.fileUploadUrl);
  const urls = yield select(getters.fileUploadUrls);

  yield all([
    yield put(actions.setFileUploadUrls(R.uniq(urls.concat([url])))),
    yield put(actions.setFileUploadUrl(""))
  ]);
};

const cancelUrl = function*({ payload }) {
  const urls = yield select(getters.fileUploadUrls);
  const updatedUrls = urls.filter(url => url !== payload);

  yield all([
    yield put(actions.setFileUploadUrls(updatedUrls)),
    R.isEmpty(updatedUrls)
      ? yield put(actions.setFileUploadUploadHasFinished(false))
      : null
  ]);
};

const onProgress = function*(data) {
  yield put(actions.setFileUploadUploadHasFinished(false));
  const progress = yield select(getters.fileUploadProgress);
  const files = yield select(getters.fileUploadFiles);
  yield all([
    yield put(
      actions.setFileUploadProgress({
        ...progress,
        [data.payload.uploadId]: {
          totalPercent: data.totalPercent,
          id: data.payload.uploadId,
          ...data.payload
        }
      })
    ),
    !files[data.payload.uploadId]
      ? yield put(
          actions.setFileUploadFiles({
            ...files,
            [data.payload.uploadId]: {
              type: "presentation",
              version: null,
              ...data.payload
            }
          })
        )
      : null
  ]);
};
// @TODO: remove the delay and fix with a local state in the component or selector
const onFinish = function*(data) {
  const progress = yield select(getters.fileUploadProgress);
  yield put(
    actions.setFileUploadProgress({
      ...progress,
      [data.payload.uploadId]: {
        totalPercent: 100,
        id: data.payload.uploadId,
        ...data.payload
      }
    })
  );
  yield delay(2000);
  yield put(actions.setFileUploadUploadHasFinished(true));
};

const save = function*() {
  yield put(actions.setFileUploadUploadHasFinished(false));
  try {
    const { eventDetails } = yield call(getParams);
    const data = yield select(getters.fileUploadData);
    const sessionId = yield select(getters.sessionId);
    const countOfAllowedFiles = yield select(getCountOfMaxFilesToUpload);
    const countOfNonVersionFiles = R.compose(
      R.length,
      R.filter(file => !file.version)
    )(data);
    const settings = yield select(getters.settings);

    if (countOfNonVersionFiles > countOfAllowedFiles) {
      yield put(
        showSnackbar({
          message: `You have exceeded the limit of files you can upload`
        })
      );
      return false;
    }

    if (!R.isEmpty(data)) {
      let files;
      if (!settings?.bizzabo_integration_enabled) {
        files = data.map(file => {
          return {
            id: file.uploadId,
            url: file.url,
            is_external: file.is_external,
            title: file.title,
            key: file.key,
            filename: file.filename,
            mimetype: file.mimetype,
            size: file.size,
            payload: file.payload,
            parent_content_id: file.version ? file.version : null,
            type: file.type
          };
        });
      } else {
        files = data.map(file => {
          return {
            id: file.uploadId,
            url: file.url,
            is_external: file.is_external,
            title: file.title,
            key: file.key,
            filename: file.filename,
            mimetype: file.mimetype,
            size: file.size,
            payload: file.payload,
            parent_content_id: file.version ? file.version : null,
            [EDIT_FILE_TYPE_FIELD_ID.TYPE]: R.prop(
              [EDIT_FILE_TYPE_FIELD_ID.TYPE],
              file
            ),
            bizzabo_settings: {
              [EDIT_FILE_TYPE_FIELD_ID.POSITION]: R.prop(
                file,
                EDIT_FILE_TYPE_FIELD_ID.POSITION
              ),
              [EDIT_FILE_TYPE_FIELD_ID.ICON]: R.prop(
                file,
                EDIT_FILE_TYPE_FIELD_ID.ICON
              ),
              [EDIT_FILE_TYPE_FIELD_ID.DISPLAY_NAME]: R.prop(
                file,
                EDIT_FILE_TYPE_FIELD_ID.DISPLAY_NAME
              )
            }
          };
        });
      }
      yield call(apiCall, {
        method: "post",
        url: `/portal/event/${eventDetails.uuid}/session-content/${sessionId}/content/bulk`,
        data: {
          files
        }
      });

      yield put(showSnackbar({ message: "File Added" }));
    }

    yield put(actions.cleanFileUploadState());
    yield put(actions.refreshContent());
  } catch (error) {
    yield put(
      registerError([
        {
          system: error,
          user: "An error occurred while saving content"
        }
      ])
    );
  }
  return true;
};

const updateFileData = function*({ payload }) {
  // update core file line item data
  yield put(actions.setFileUploadUploadHasFinished(false));
  const data = yield select(getters.fileUploadData);
  const newData = data.map(file => {
    if (file.uploadId === payload.uploadId) {
      return {
        ...file,
        version: payload.type === "versions" ? payload.value : file.version,
        type: payload.type === "type" ? payload.value : file.type
      };
    }
    return file;
  });
  yield put(actions.setFileUploadData(newData));
  // update pre-uploaded files
  const files = yield select(getters.fileUploadFiles);

  const newFiles = {
    ...files,
    [payload.uploadId]: {
      ...files[payload.uploadId],
      version:
        payload.type === "versions"
          ? payload.value
          : files[payload.uploadId].version,
      type:
        payload.type === "type" ? payload.value : files[payload.uploadId].type
    }
  };
  yield put(actions.setFileUploadFiles(newFiles));
  yield put(actions.setFileUploadUploadHasFinished(true));
};

const removeUploadedUrls = function*({ payload }) {
  const uploadedUrls = yield select(getters.fileUploadUploadedUrls);
  if (payload) {
    const newUploadedUrls = uploadedUrls.filter(url => url !== payload);
    yield put(actions.setFileUploadUploadedUrls(newUploadedUrls));
  }
};

const watchInit = function*() {
  yield takeEvery(actions.initFileDropzone.type, init);
};

const watchRemoveUploadedUrls = function*() {
  yield takeEvery(
    actions.fileUploadRemoveUploadedUrls.type,
    removeUploadedUrls
  );
};

const watchSave = function*() {
  yield takeEvery(actions.saveFileUpload.type, save);
};

const watchCancelUpload = function*() {
  yield takeEvery(actions.cancelFileUpload.type, cancelUpload);
};

const watchValidateUrl = function*() {
  yield takeEvery(actions.validateFileUploadUrl.type, validateUrl);
};

const watchAddUrl = function*() {
  yield takeEvery(actions.addFileUploadUrl.type, addUrl);
};

const watchCancelUrl = function*() {
  yield takeEvery(actions.cancelFileUploadUrl.type, cancelUrl);
};

const watchOnProgress = function*() {
  yield takeEvery(actions.onFileUploadProgress.type, onProgress);
};

const watchOnFinished = function*() {
  yield takeEvery(actions.onFileUploadFinished.type, onFinish);
};

const watchUpdateFileData = function*() {
  yield takeEvery(actions.updateFileUploadFileData.type, updateFileData);
};

const rootSaga = function*() {
  yield all([
    fork(watchInit),
    fork(watchUpdateFileData),
    fork(watchOnProgress),
    fork(watchOnFinished),
    fork(watchCancelUpload),
    fork(watchValidateUrl),
    fork(watchAddUrl),
    fork(watchCancelUrl),
    fork(watchRemoveUploadedUrls),
    fork(watchSave)
  ]);
};

export default rootSaga;
